/**
 * Licence pricing, as Otii invoices it in Xero.
 *
 * Rates are £ per site, excluding VAT, for the contract's own period: a year
 * on an annual contract, a quarter on a quarterly one, as Xero's items are
 * priced. The first site is always charged at the base (lowest) tier's rate;
 * every additional site is charged at the rate of the tier that the *total*
 * site count falls into: 20 sites are one first site and 19 at the 15–49 rate.
 */

export type BillingFrequency = "annual" | "quarterly";

export const FREQUENCIES: { id: BillingFrequency; label: string; invoicesPerYear: number }[] = [
  { id: "annual", label: "Annual", invoicesPerYear: 1 },
  { id: "quarterly", label: "Quarterly", invoicesPerYear: 4 },
];

export interface PricingTier {
  id: string;
  label: string;
  minSites: number;
  /** null means "and above". */
  maxSites: number | null;
  /** £ per site per year (exc VAT) on an annual contract. */
  annualRate: number;
  /** £ per site per quarter (exc VAT) on a quarterly contract. */
  quarterlyRate: number;
  /** The Xero item codes for this tier's lines, e.g. Annual-Multi-Site-Tier-1. */
  annualCode: string | null;
  quarterlyCode: string | null;
  sortOrder: number;
}

export interface Quote {
  sites: number;
  frequency: BillingFrequency;
  /** £ per site for the contract's period (a year, or a quarter), exc VAT. */
  firstSiteRate: number;
  additionalRate: number;
  additionalTier: PricingTier | null;
  monthlyExVat: number;
  annualExVat: number;
  invoicesPerYear: number;
  /** Value of one invoice, exc VAT. */
  invoiceExVat: number;
  vatRate: number;
  annualIncVat: number;
}

export const round2 = (n: number) => Math.round(n * 100) / 100;

export function sortTiers(tiers: PricingTier[]): PricingTier[] {
  return tiers.slice().sort((a, b) => a.minSites - b.minSites);
}

export function tierForSites(tiers: PricingTier[], sites: number): PricingTier | null {
  const sorted = sortTiers(tiers);
  for (const t of sorted) {
    const hi = t.maxSites ?? Infinity;
    if (sites >= t.minSites && sites <= hi) return t;
  }
  return sorted[sorted.length - 1] ?? null;
}

/**
 * How a contract is invoiced. A quarterly contract is always invoiced
 * quarterly; an annual contract is invoiced annually unless quarterly
 * invoicing has been agreed. The price always follows the contract term.
 */
export function invoiceScheduleFor(term: BillingFrequency, schedule: BillingFrequency | null | undefined): BillingFrequency {
  return term === "quarterly" ? "quarterly" : (schedule ?? "annual");
}

export const invoicesPerYearFor = (schedule: BillingFrequency) => (schedule === "annual" ? 1 : 4);

/** Short label for a customer's contract: term, plus the invoicing when it differs. */
export function contractLabel(term: BillingFrequency, schedule: BillingFrequency): string {
  if (term === "quarterly") return "Quarterly contract";
  return schedule === "quarterly" ? "Annual contract, invoiced quarterly" : "Annual contract";
}

function rateFor(tier: PricingTier | null, frequency: BillingFrequency): number {
  if (!tier) return 0;
  return frequency === "annual" ? tier.annualRate : tier.quarterlyRate;
}

export function quote(
  tiers: PricingTier[],
  vatRate: number,
  sites: number,
  frequency: BillingFrequency,
): Quote {
  const n = Math.max(0, Math.floor(sites || 0));
  const invoicesPerYear = frequency === "annual" ? 1 : 4;
  const base = sortTiers(tiers)[0] ?? null;
  const additionalTier = n > 1 ? tierForSites(tiers, n) : null;
  const firstSiteRate = rateFor(base, frequency);
  const additionalRate = rateFor(additionalTier, frequency);
  const perPeriod = n === 0 ? 0 : firstSiteRate + (n - 1) * additionalRate;
  const annualExVat = round2(perPeriod * (frequency === "annual" ? 1 : 4));
  return {
    sites: n,
    frequency,
    firstSiteRate,
    additionalRate,
    additionalTier,
    monthlyExVat: round2(annualExVat / 12),
    annualExVat,
    invoicesPerYear,
    invoiceExVat: round2(annualExVat / invoicesPerYear),
    vatRate,
    annualIncVat: round2(annualExVat * (1 + vatRate)),
  };
}

/**
 * Annual value of moving from `fromSites` to `toSites`. Used for expansion
 * deals and unrealised potential, where tier breaks mean the marginal value
 * is not simply (sites × rate).
 */
export function marginalAnnualValue(
  tiers: PricingTier[],
  fromSites: number,
  toSites: number,
  frequency: BillingFrequency,
): number {
  if (toSites <= fromSites) return 0;
  return round2(quote(tiers, 0, toSites, frequency).annualExVat - quote(tiers, 0, fromSites, frequency).annualExVat);
}

/** A licence line on an invoice, as raised in Xero. */
export interface InvoiceLine {
  /** The Xero item code, when the tier has one. */
  code: string | null;
  description: string;
  quantity: number;
  /** £ per site for the invoice, exc VAT. */
  unitPrice: number;
  /** % off, as Xero's line discount. */
  discountPct: number | null;
  /** Quantity × unit price, less the discount. */
  amount: number;
}

/**
 * One invoice's licence lines. An annual contract invoiced quarterly carries
 * the annual items at a quarter of their price.
 */
export function invoiceLines(
  tiers: PricingTier[],
  sites: number,
  term: BillingFrequency,
  schedule: BillingFrequency,
  discountPct: number | null = null,
): InvoiceLine[] {
  const q = quote(tiers, 0, sites, term);
  const split = term === "annual" && schedule === "quarterly" ? 4 : 1;
  const codeOf = (t: PricingTier | null | undefined) => (t ? (term === "annual" ? t.annualCode : t.quarterlyCode) : null);
  const line = (code: string | null, description: string, quantity: number, rate: number): InvoiceLine => {
    const unitPrice = round2(rate / split);
    return { code, description, quantity, unitPrice, discountPct, amount: round2(quantity * unitPrice * (1 - (discountPct ?? 0) / 100)) };
  };
  const out: InvoiceLine[] = [];
  if (q.sites >= 1) out.push(line(codeOf(sortTiers(tiers)[0]), "First site", 1, q.firstSiteRate));
  if (q.sites > 1) {
    const t = q.additionalTier;
    const band = t ? (t.maxSites === null ? `${t.minSites}+ sites` : `${t.minSites}–${t.maxSites} sites`) : "";
    out.push(line(codeOf(t), band ? `Additional sites (${band})` : "Additional sites", q.sites - 1, q.additionalRate));
  }
  return out;
}
